<template>
  <v-row dense no-gutters>
    <v-col cols="12">
      <v-card>
        <v-card-title class="pb-0">
          <h1 class="headline font-weight-medium">
            {{ isNew ? "Створення продукту" : "Продукт" }}
          </h1>
          <v-spacer />
          <ActionsModalWindow
            v-if="modal"
            :id="id"
            component-name="Product"
            @close="$emit('close')"
          />
        </v-card-title>
        <v-form v-model="valid">
          <v-card-text class="py-0">
            <v-row>
              <v-col cols="8" class="pb-0">
                <CustomTextField
                  v-model="title"
                  label="Найменування*"
                  :error-messages="$v.title.$error ? errors.TITLE : []"
                  :hide-details="false"
                  @input="$v.title.$touch()"
                />
              </v-col>
              <v-col cols="4">
                <DescriptionLink
                  v-model="description"
                  label="Опис"
                  placeholder="Введіть опис"
                />
              </v-col>
            </v-row>

            <v-row class="mb-n4">
              <v-col md="3" cols="12" class="py-0">
                <CustomTextField
                  v-model="article"
                  label="Артикул"
                  :loading="loadingArticle"
                  required
                  :error-messages="$v.article.$error ? errors.ARTICLE : []"
                  :hide-details="false"
                  @input="$v.title.$touch()"
                >
                  <template v-slot:progress>
                    <v-progress-linear
                      v-if="loading"
                      color="brown darken-4"
                      indeterminate
                      rounded
                      height="10"
                    />
                  </template>
                </CustomTextField>
              </v-col>

              <v-col md="3" cols="12" class="py-0">
                <DecimalEditor
                  v-model="amount"
                  :scale="2"
                  :error-messages="$v.amount.$error ? errors.AMOUNT : []"
                  :hide-details="false"
                  label="Ціна продажу*, грн без ПДВ"
                  @input="$v.title.$touch()"
                />
              </v-col>

              <v-col md="3" cols="12" class="py-0">
                <DecimalEditor
                  v-model="purchaseCost"
                  :scale="2"
                  label="Ціна закупівлі, грн без ПДВ"
                  @input="isChanged = true"
                />
              </v-col>

              <v-col md="3" cols="12" class="py-0">
                <ProductCategorySelect
                  v-model="category"
                  :error-messages="$v.category.$error ? errors.AMOUNT : []"
                  :hide-details="false"
                  label="Категорія продукту*"
                  @input="$v.title.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <ProductType
                  v-model="type"
                  :is-new="isNew"
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="comments"
                  class="pt-0"
                  hide-details
                  label="Примітки"
                  dense
                  outlined
                  @input="isChanged = true"
                />
              </v-col>
            </v-row>
            <ProductClients
              v-if="!isNew"
              :id="id"
              :track-versions="trackVersions"
            />

            <ProductVersions
              v-if="!isNew"
              :value="versions"
              :track-versions="trackVersions"
              @add="addVersion"
              @remove="removeVersion"
            >
              <template v-slot:trackVersions>
                <v-col cols="auto" class="ml-3">
                  <v-switch
                    v-model="trackVersions"
                    label="Не відслідковувати версії"
                    class="mx-1 mb-n4"
                    color="green"
                    dense
                  />
                </v-col>
              </template>
            </ProductVersions>

            <v-card-actions class="pl-0 pt-0">
              <PrimaryBtn
                :has-warnings="$v.$invalid"
                :loading="loading"
                @click="save"
              />
              <v-btn v-if="!modal" @click="$router.go(-1)">
                Відмінити
              </v-btn>
            </v-card-actions>
          </v-card-text>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import DecimalEditor from "../../../components/DecimalEditor.vue";
import ProductClients from "./ProductClients.vue";
import ProductVersions from "./ProductVersions.vue";
import DescriptionLink from "@/components/DescriptionLink.vue";
import ProductCategorySelect from "../../catalogs/product-categories/ProductCategorySelect.vue";
import * as productsApi from "../../../backend/productsApi.js";
import ProductType from "./ProductType.vue";
import PrimaryBtn from "@/components/PrimaryBtn.vue";
import { productTypes } from "@/helpers";

import { ERRORS } from "@/helpers";
import { required, minLength, requiredIf } from "vuelidate/lib/validators";

export default {
  components: {
    DecimalEditor,
    ProductClients,
    ProductVersions,
    ProductCategorySelect,
    DescriptionLink,
    ProductType,
    PrimaryBtn,
    ActionsModalWindow() {
      return import("@/components/ActionsModalWindow.vue");
    },
  },
  props: ["id", "modal"],
  data: () => ({
    valid: false,
    title: "",
    article: "",
    amount: "",
    comments: "",
    description: "",
    loading: false,
    isChanged: false,
    versions: [],
    actualVersion: "",
    purchaseCost: 0,
    category: null,
    type: null,
    articleUnique: true,
    loadingArticle: false,
    trackVersions: false,
  }),

  validations: {
    title: {
      required,
      minLength: minLength(3),
    },
    article: {
      required: requiredIf(function() {
        return this.articleUnique;
      }),
    },
    amount: {
      minValue(value) {
        return value > 0;
      },
    },
    category: {
      required,
    },
  },

  computed: {
    isNew() {
      return +this.id ? false : true;
    },
    productTypes() {
      return productTypes;
    },
    errors() {
      return {
        TITLE: [ERRORS.REQUIRED],
        ARTICLE: [ERRORS.REQUIRED],
        AMOUNT: [ERRORS.REQUIRED],
        CATEGORY: [ERRORS.REQUIRED],
      };
    },
  },
  watch: {
    article: {
      handler(val, oldVal) {
        if (val !== oldVal && val !== "") {
          this.validateArticle(val);
        }
      },
    },
    trackVersions: {
      handler(val, oldVal) {
        if (
          oldVal === true &&
          val === false &&
          this.type === this.productTypes.LICENSE.value
        ) {
          this.trackVersions = false;
        }
      },
    },
    type: {
      handler(val, oldVal) {
        if (
          oldVal === null &&
          val === this.productTypes.LICENSE.value &&
          this.trackVersions === false
        ) {
          this.trackVersions = false;
        }
        if (
          oldVal !== null &&
          val === this.productTypes.LICENSE.value &&
          this.trackVersions === false
        ) {
          this.trackVersions = true;
        }
      },
    },
  },
  created() {
    if (+this.id) {
      productsApi.getProduct(this.id).then((product) => {
        this.title = product.title;
        this.article = product.article;
        this.amount = product.amount;
        this.comments = product.comments;
        this.description = product.description;
        this.versions = product.productVersions;
        this.actualVersion = product.actualVersion;
        this.purchaseCost = product.purchaseCost;
        this.category = product.category;
        this.type = product.type;
        this.trackVersions = !product.trackVersions;
      });
    }
  },
  methods: {
    async save() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.loading = true;
      const product = {
        id: +this.id,
        title: this.title,
        article: this.article,
        amount: this.amount,
        comments: this.comments,
        description: this.description,
        productVersions: this.versions,
        purchaseCost: this.purchaseCost,
        categoryId: this.category?.id ?? null,
        type: this.type,
        trackVersions: !this.trackVersions,
      };

      try {
        if (!this.isNew) {
          await productsApi.updateProduct(this.id, product);
        } else {
          await productsApi.createProduct(product);
        }
        this.$notifySuccess();
        this.modal ? this.$emit("close") : this.$router.go(-1);
      } finally {
        this.loading = false;
        this.isChanged = false;
      }
    },
    addVersion(version) {
      const productVersion = {
        releaseDate: version.releaseDate,
        version: version.version,
        notes: version.notes,
        productId: this.id,
      };
      productsApi.createProductVersion(this.id, productVersion);

      this.versions.push(version);
      this.versions.sort((a, b) => {
        return new Date(b.releaseDate) - new Date(a.releaseDate);
      });

      return this.versions;
    },
    removeVersion(versionId) {
      productsApi.deleteProductVersion(versionId);

      const index = this.versions.findIndex((v) => v.id === versionId);
      this.versions.splice(index, 1);
      this.versions.sort((a, b) => {
        return new Date(b.releaseDate) - new Date(a.releaseDate);
      });
    },
    async validateArticle(article) {
      this.loadingArticle = true;
      this.articleUnique = await productsApi.validateArticle(article, this.id);
      if (!this.articleUnique) {
        this.$notifyWarning("Продукт з даним артикулем вже існує");
      }
      this.loadingArticle = false;
    },
  },
};
</script>

<style lang="scss">
.articleMessage {
  background-color: #ffffff;
  color: #ff5252;
  font-size: 16px;
  border: 2px solid #ff5252;
  padding: 10px;
  margin: 25px 15px;
}

.error--text .v-input__slot .v-text-field__slot input {
  color: inherit;
}
</style>
